import moment from 'moment/min/moment-with-locales';
/**
 * Rather than do some momentjs work, which can be slow and we'd have to adjust for timezone,
 * just interpolate the string
 * @param iso8601Date
 */
export function addZuluTimeToISO8601Date(iso8601Date: string) {
  return `${iso8601Date}T00:00:00Z`;
}

const MIDNIGHT_UTC_REGEX = /^\d{4}-\d{2}-\d{2}T00:00:00(\.0+)?Z$/;

/**
 * Maps unsupported locale codes to Moment.js-supported equivalents.
 * Moment.js does not support 'zh-hant' but supports 'zh-tw' and 'zh-cn'.
 */
const momentLocaleOverrides: Record<string, string> = {
  'zh-hant': 'zh-tw',
};

/**
 * Normalizes locale codes for Moment.js compatibility by applying overrides for unsupported locales.
 * Currently maps 'zh-hant' to 'zh-tw' since Moment.js does not support Traditional Chinese directly.
 * @param language - The locale code from i18n (e.g., 'zh-hant', 'en')
 * @returns Normalized locale code compatible with Moment.js
 */
export const normalizeMomentLocale = (language: string): string => momentLocaleOverrides[language] ?? language;

/**
 * Normalizes midnight UTC timestamps to date-only strings to avoid timezone shift issues.
 * For example, '2024-01-15T00:00:00Z' becomes '2024-01-15T00:00:00' to prevent off-by-1 day errors
 * when parsing in local timezones behind UTC.
 */
export const normalizeMidnightUTC = (value: string): string =>
  MIDNIGHT_UTC_REGEX.test(value) ? value.slice(0, -1) : value;

/**
 * Locale aware formats
 * Tokens are case-sensitive: https://momentjs.com/docs/#/parsing/string
 * L --> MM/DD/YYYY
 * LT --> HH:MM AM
 * 'MMM Do, YYYY' --> e.g., Nov 20th, 2020
 * @param value represents date string
 * @param format 'L LT' would return MM/DD/YYYY HH:MM AM
 * @returns formatted date string, or null if value is not a valid date
 */
export const momentFormatter = (value: string, format: string, language?: string): string | null => {
  const date = moment(normalizeMidnightUTC(value));
  const dateIsValid = date.isValid();
  if (!dateIsValid) {
    return null;
  }
  return language ? date.locale(normalizeMomentLocale(language)).format(format) : date.format(format);
};

/**
 *
 * @param numDaysFromToday number of days from today
 * @returns modified date object shifted according to number of days specified
 */
export const getDateNumDaysFromToday = (numDaysFromToday: number) => {
  const targetDate = new Date();
  targetDate.setDate(targetDate.getDate() + numDaysFromToday);
  return targetDate; // modified
};

export const utcFormat = 'YYYY-MM-DDTHH:mm:ss[Z]';

/**
 * Formats date string value to UTC Timestamp start of day
 * @param value YYYY-MM-DD
 * @constructor
 * returns YYYY-MM-DDT00:00:00Z
 */
export const ISO8601DateToUTCTimestamp = (value: string) =>
  moment.utc(value, 'YYYY-MM-DD').startOf('day').format(utcFormat);

/**
 * Formats a date string using locale-aware formatting.
 * Uses moment's locale feature to display dates in the user's preferred format.
 *
 * @param value - Date string or Date object to format
 * @param locale - Locale code (e.g., 'en', 'de', 'fr', 'zh') - typically from i18n.language
 * @param format - Moment format token (default: 'L' for locale short date)
 *   - 'L' ? locale short date (e.g., 12/08/2024 for en, 08.12.2024 for de)
 *   - 'LL' ? locale long date (e.g., December 8, 2024)
 *   - 'LLL' ? locale long date with time
 *   - 'LLLL' ? locale full date with day of week
 * @returns Formatted date string
 *
 * @example
 * formatLocalizedDate('2024-12-08', 'en') // '12/08/2024'
 * formatLocalizedDate('2024-12-08', 'de') // '08.12.2024'
 * formatLocalizedDate('2024-12-08', 'fr') // '08/12/2024'
 * formatLocalizedDate('2024-12-08', 'en', 'LL') // 'December 8, 2024'
 */
export const formatLocalizedDate = (value: string | Date, locale: string, format: string = 'L'): string =>
  moment(value).locale(normalizeMomentLocale(locale)).format(format);

/**
 * Formats a YYYY_MM date string to a localized month-year display format.
 * Used for cashflow chart/table axis labels and column headers.
 *
 * @param date - Date string in 'YYYY_MM' format (e.g., '2024_01')
 * @param language - Optional locale code for localization.
 * @returns Formatted date string (e.g., 'Jan 24')
 */
export const formatYearMonthLabel = (date: string, language?: string): string => {
  if (language) {
    return (
      momentFormatter(date.replace('_', '-') + '-01', 'MMM YY', language) ??
      moment(date, 'YYYY_MM').locale(normalizeMomentLocale(language)).format('MMM YY')
    );
  }
  return moment(date, 'YYYY_MM').format('MMM YY');
};

/**
 * Formats a date value to 'D-MMM-YYYY' format (e.g., '15-Jan-2024').
 * Used for simulated historical returns chart axis labels and tooltips.
 *
 * @param value - Date value (timestamp number or date string)
 * @param language - Locale code for localization
 * @returns Formatted date string
 */
export const formatDayMonthYear = (value: string | number, language: string): string =>
  momentFormatter(String(value), 'D-MMM-YYYY', language) ??
  moment(value).locale(normalizeMomentLocale(language)).format('D-MMM-YYYY');

/**
 * Formats a date value to 'MMM YYYY' format (e.g., 'Jan 2024').
 * Used for simulated historical returns period labels.
 *
 * @param value - Date value (date string or Date object)
 * @param language - Locale code for localization
 * @returns Formatted date string
 */
export const formatMonthYear = (value: string | number | Date, language: string): string =>
  momentFormatter(String(value), 'MMM YYYY', language) ??
  moment(value).locale(normalizeMomentLocale(language)).format('MMM YYYY');
